/**
 * Klaviyo Event Listeners
 *
 * Uses the unified event system (pmw:pixel:*) with firing decisions.
 *
 * @see https://developers.klaviyo.com/en/docs/javascript_api
 */

(function (pmw, undefined) {

	/**
	 * Send an adapted Klaviyo event, after the shared guards.
	 *
	 * @param {Object} payload - The pmw:pixel:* event payload
	 * @returns {Object|null} The adapted event, when it was sent
	 */
	const trackFromPayload = (payload) => {
		if (!pmwDataLayer?.pixels?.klaviyo?.loaded) return null;
		if (!pmw.shouldFireBrowserPixel(payload, "klaviyo")) return null;

		try {
			const klaviyoData = payload.pixels?.klaviyo;
			if (!klaviyoData) return null;

			pmw.klaviyoTrack(klaviyoData.event_name, klaviyoData.properties, klaviyoData.unique_id, klaviyoData.value);

			return klaviyoData;
		} catch (e) {
			console.error(e);
			return null;
		}
	};

	/**
	 * Load pixel on consent
	 *
	 * Klaviyo is an email and SMS marketing platform, so it is gated on
	 * marketing consent. This is the gate the Klaviyo plugin never had.
	 */
	pmw.bus.on("pmw:load-pixels", function () {
		if (
			pmwDataLayer?.pixels?.klaviyo?.public_api_key &&
			!pmwDataLayer?.pixels?.klaviyo?.loaded &&
			pmw.consent.canPixelBeFired("marketing", "Klaviyo")
		) {
			pmw.loadKlaviyoPixel();
		}
	});

	/**
	 * Viewed Collection event
	 *
	 * The Pixel Manager emits one view_item_list event per product that came
	 * into view. They are collected here and sent as a single Klaviyo event,
	 * so a listing page produces one "Viewed Collection" instead of dozens.
	 */
	let klaviyoListItems   = [];
	let klaviyoListName    = undefined;
	let klaviyoListTimeout = null;

	pmw.bus.on("pmw:pixel:view-item-list", (payload) => {
		if (!pmwDataLayer?.pixels?.klaviyo?.loaded) return;
		if (!pmw.shouldFireBrowserPixel(payload, "klaviyo")) return;

		try {
			const klaviyoData = payload.pixels?.klaviyo;
			if (!klaviyoData?.properties?.Items?.length) return;

			klaviyoListItems = klaviyoListItems.concat(klaviyoData.properties.Items);
			klaviyoListName  = klaviyoData.properties.CollectionName || klaviyoListName;

			if (klaviyoListTimeout) clearTimeout(klaviyoListTimeout);

			klaviyoListTimeout = setTimeout(() => {

				const items    = klaviyoListItems;
				const listName = klaviyoListName;

				klaviyoListItems   = [];
				klaviyoListName    = undefined;
				klaviyoListTimeout = null;

				if (!items.length) return;

				pmw.klaviyoTrack("Viewed Collection", {
					CollectionName: listName,
					ItemNames     : items.map(item => item.ProductName),
					ItemCount     : items.length,
					Items         : items,
				});
			}, 500);
		} catch (e) {
			console.error(e);
		}
	});

	/**
	 * Selected Product event
	 */
	pmw.bus.on("pmw:pixel:select-item", (payload) => {
		trackFromPayload(payload);
	});

	/**
	 * Viewed Product event
	 *
	 * Also feeds klaviyo.js's recently viewed store, which browse abandonment
	 * flows read from. The adapter returns null in gap-fill mode, where the
	 * Klaviyo plugin sends this event itself.
	 */
	pmw.bus.on("pmw:pixel:view-item", (payload) => {
		const klaviyoData = trackFromPayload(payload);
		if (klaviyoData?.viewed_item) pmw.klaviyoTrackViewedItem(klaviyoData.viewed_item);
	});

	/**
	 * Added to Cart event
	 */
	pmw.bus.on("pmw:pixel:add-to-cart", (payload) => {
		trackFromPayload(payload);
	});

	/**
	 * Removed from Cart event
	 */
	pmw.bus.on("pmw:pixel:remove-from-cart", (payload) => {
		trackFromPayload(payload);
	});

	/**
	 * Viewed Cart event
	 */
	pmw.bus.on("pmw:pixel:view-cart", (payload) => {
		trackFromPayload(payload);
	});

	/**
	 * Started Checkout event
	 *
	 * The visitor is identified first, from the email typed into the checkout
	 * when there is one, so the event lands on their profile. Abandoned cart
	 * flows trigger on exactly this event.
	 */
	pmw.bus.on("pmw:pixel:begin-checkout", (payload) => {
		pmw.klaviyoIdentify();
		trackFromPayload(payload);
	});

	/**
	 * Added Shipping Info event
	 */
	pmw.bus.on("pmw:pixel:add-shipping-info", (payload) => {
		pmw.klaviyoIdentify();
		trackFromPayload(payload);
	});

	/**
	 * Added Payment Info event
	 */
	pmw.bus.on("pmw:pixel:add-payment-info", (payload) => {
		pmw.klaviyoIdentify();
		trackFromPayload(payload);
	});

	/**
	 * Added to Wishlist event
	 */
	pmw.bus.on("pmw:pixel:add-to-wishlist", (payload) => {
		trackFromPayload(payload);
	});

	/**
	 * Submitted Search event
	 */
	pmw.bus.on("pmw:pixel:search", (payload) => {
		trackFromPayload(payload);
	});

	/**
	 * Placed Order event
	 *
	 * The adapter returns null unless the Pixel Manager runs standalone
	 * without the Events API, because the purchase is otherwise reported
	 * server-side, by the Events API or by Klaviyo's own order sync. When it
	 * does fire, the Ordered Product events follow, one per line item.
	 */
	pmw.bus.on("pmw:pixel:purchase", (payload) => {
		pmw.klaviyoIdentify();

		const klaviyoData = trackFromPayload(payload);
		if (!klaviyoData?.ordered_products) return;

		klaviyoData.ordered_products.forEach(orderedProduct => {
			pmw.klaviyoTrack(orderedProduct.event_name, orderedProduct.properties, orderedProduct.unique_id, orderedProduct.value);
		});
	});

}(window.pmw = window.pmw || {}));
